import { useCheckDuplication } from '@/remote/site-management/duplication-check';
import { useResetSiteMotor } from '@/remote/site-management/motor-control';
import { useMeQuery } from '@/remote/user-management/fetch-me';
import type { CreateSiteData, Site } from '@/types/site';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface BasicInfoTabProps {
  isEditMode?: boolean;
}

export default function BasicInfoTab({
  isEditMode = false,
}: BasicInfoTabProps) {
  const resetMotorMutation = useResetSiteMotor();
  const [debouncedSiteId, setDebouncedSiteId] = useState('');
  const [originalSiteId, setOriginalSiteId] = useState<string>('');
  const { data: me } = useMeQuery();

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<Site | CreateSiteData>();

  const siteId = watch('siteId');

  // 편집 모드일 때 초기 siteId 저장
  useEffect(() => {
    if (isEditMode && siteId && !originalSiteId) {
      setOriginalSiteId(siteId);
    }
  }, [isEditMode, siteId, originalSiteId]);

  // 중복 확인 조건 결정
  const shouldCheckDuplication = () => {
    // 생성 모드: siteId가 있고 에러가 없을 때
    if (!isEditMode) {
      return debouncedSiteId.length > 0 && !errors.siteId;
    }

    // 편집 모드: 기존 siteId와 다르고, siteId가 있고, 에러가 없을 때
    return (
      debouncedSiteId.length > 0 &&
      !errors.siteId &&
      originalSiteId &&
      debouncedSiteId !== originalSiteId
    );
  };

  // 중복 확인 실행
  const {
    data: isDuplicate,
    isLoading: isCheckingDuplicate,
    error: duplicateError,
  } = useCheckDuplication(debouncedSiteId, {
    enabled: Boolean(shouldCheckDuplication()),
  });

  // 디바운싱 처리
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSiteId(siteId || '');
    }, 500);

    return () => clearTimeout(timer);
  }, [siteId]);

  const handleResetMotor = async () => {
    if (!siteId) {
      alert('사이트 ID가 필요합니다.');
      return;
    }

    if (!confirm('모터를 리셋하시겠습니까?')) {
      return;
    }

    try {
      await resetMotorMutation.mutateAsync(siteId);
      alert('모터가 성공적으로 리셋되었습니다.');
    } catch (error) {
      console.error('모터 리셋 실패:', error);
      alert('모터 리셋에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          현장 ID <span className="text-red-500">(필수)</span>
        </label>
        <input
          type="text"
          {...register('siteId', {})}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
            errors.siteId
              ? 'border-red-300 focus:ring-red-500'
              : isDuplicate
                ? 'border-red-300 focus:ring-red-500'
                : isDuplicate === false
                  ? 'border-green-300 focus:ring-green-500'
                  : 'border-gray-300'
          }`}
          disabled={
            me?.userLevel !== 'SUPER_ADMINISTRATOR' &&
            me?.userLevel !== 'HEADQUARTERS_MANAGER'
          }
          minLength={6}
          maxLength={6}
          value={siteId}
        />
        {errors.siteId && (
          <p className="mt-1 text-sm text-red-600">{errors.siteId.message}</p>
        )}

        {/* 중복 확인 메시지 - 중복 체크 조건을 만족할 때만 표시 */}
        {debouncedSiteId && !errors.siteId && shouldCheckDuplication() && (
          <div className="mt-1">
            {isCheckingDuplicate ? (
              <p className="text-sm text-gray-500 flex items-center">
                <span className="animate-spin mr-2 h-3 w-3 border border-gray-300 border-t-blue-500 rounded-full"></span>
                중복 확인 중...
              </p>
            ) : duplicateError ? (
              <p className="text-sm text-yellow-600">
                중복 확인 중 오류가 발생했습니다.
              </p>
            ) : isDuplicate === true ? (
              <p className="text-sm text-red-600 flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                이미 사용 중인 현장 ID입니다.
              </p>
            ) : isDuplicate === false ? (
              <p className="text-sm text-green-600 flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                사용 가능한 현장 ID입니다.
              </p>
            ) : null}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          현장명 <span className="text-red-500">(필수)</span>
        </label>
        <input
          type="text"
          {...register('name')}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
            errors.name
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-300'
          }`}
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          관리번호
        </label>
        <input
          type="text"
          {...register('managementNumber')}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          CDMA 번호 <span className="text-red-500">(필수)</span>
        </label>
        <input
          type="text"
          {...register('cdmaNumber')}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          disabled={
            me?.userLevel !== 'SUPER_ADMINISTRATOR' &&
            me?.userLevel !== 'HEADQUARTERS_MANAGER'
          }
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          관리 분류
        </label>
        <Controller
          name="managementType"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">선택하세요</option>
              <option value="농업기반시설">농업기반시설</option>
              <option value="상수도">상수도</option>
              <option value="가압장">가압장</option>
              <option value="기타">기타</option>
            </select>
          )}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          시설 유형
        </label>
        <Controller
          name="facilityType"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">선택하세요</option>
              <option value="자연유하">자연유하</option>
              <option value="양수저장">양수저장</option>
              <option value="압력펌프">압력펌프</option>
              <option value="기타">기타</option>
            </select>
          )}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          취수원 분류
        </label>
        <Controller
          name="waterIntakeType"
          control={control}
          render={({ field }) => (
            <select
              {...field}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">선택하세요</option>
              <option value="지하수">지하수</option>
              <option value="계곡수">계곡수</option>
              <option value="지하수+계곡수">지하수+계곡수</option>
              <option value="양수장">양수장</option>
              <option value="기타">기타</option>
            </select>
          )}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          설치일자
        </label>
        <input
          type="date"
          {...register('installedDate')}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          탱크 용량 (톤)
        </label>
        <input
          type="number"
          {...register('tankSize', {
            valueAsNumber: true,
          })}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 ${
            errors.tankSize
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-300'
          }`}
        />
        {errors.tankSize && (
          <p className="mt-1 text-sm text-red-600">{errors.tankSize.message}</p>
        )}
      </div>

      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          모터제원
        </label>
        <textarea
          {...register('motorSpecification')}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* 모터 관리 - 편집 모드일 때만 표시 */}
      {isEditMode && siteId && (
        <div className="md:col-span-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              모터 관리
            </label>
          </div>
          <button
            type="button"
            onClick={handleResetMotor}
            disabled={
              resetMotorMutation.isPending ||
              !siteId ||
              (me?.userLevel !== 'SUPER_ADMINISTRATOR' &&
                me?.userLevel !== 'HEADQUARTERS_MANAGER')
            }
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resetMotorMutation.isPending ? '리셋 중...' : '모터 리셋'}
          </button>
          <p className="mt-2 text-xs text-gray-500">
            모터를 강제로 리셋합니다. 이 작업은 되돌릴 수 없습니다.
          </p>
        </div>
      )}
    </div>
  );
}
